/**
 * Who can see this client's portal: invite someone by email, see who is in
 * and who has not accepted yet, and remove access. A client login sees only
 * what is shared with this client — the invitation says so, and so does this
 * panel.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { agencyApi, type ClientInvitation, type ClientPerson } from './agency-api.ts';
import type { Client } from './reports-api.ts';

const day = new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short' });

export function ClientAccess({
  organisationId,
  client,
}: {
  organisationId: string;
  client: Client;
}): ReactNode {
  const [people, setPeople] = useState<ClientPerson[] | null>(null);
  const [invitations, setInvitations] = useState<ClientInvitation[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const load = () =>
    agencyApi
      .people(organisationId, client.id)
      .then((data) => {
        setPeople(data.people);
        setInvitations(data.invitations);
      })
      .catch(() => setPeople([]));
  useEffect(() => {
    setPeople(null);
    setMessage(null);
    void load();
  }, [organisationId, client.id]);

  const act = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage({ tone: 'ok', text: success });
      await load();
    } catch (error) {
      setMessage({
        tone: 'error',
        text: error instanceof Error ? error.message : 'That did not work. Try again.',
      });
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };

  const invite = (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    void act(async () => {
      await agencyApi.invite(organisationId, client.id, address);
      setEmail('');
    }, `Invitation sent to ${address}.`);
  };

  return (
    <section className="card rh-access" aria-label="Client access">
      <h2>Client access</h2>
      <p className="panel-note">
        People at {client.name} who can sign in and see the dashboards and reports you share.
        Nothing else.
      </p>
      {message && (
        <p
          className={`rh-access-msg rh-access-${message.tone}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}
      <ul className="rh-access-list">
        {people === null && <li className="rh-source-none">Loading…</li>}
        {people?.length === 0 && invitations.length === 0 && (
          <li className="rh-source-none">No one yet.</li>
        )}
        {people?.map((person) => (
          <li key={person.user_id}>
            <span>
              <strong>{person.name ?? person.email}</strong>
              {person.name && <small>{person.email}</small>}
            </span>
            {removing === person.user_id ? (
              <span className="rh-access-confirm">
                <button
                  type="button"
                  className="btn small danger"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => agencyApi.removeLogin(organisationId, client.id, person.user_id),
                      `${person.email} can no longer sign in.`,
                    )
                  }
                >
                  Remove
                </button>
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => setRemoving(null)}
                >
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="rh-text-btn"
                onClick={() => setRemoving(person.user_id)}
              >
                Remove
              </button>
            )}
          </li>
        ))}
        {invitations.map((invitation) => (
          <li key={invitation.id}>
            <span>
              <strong>{invitation.email}</strong>
              <small>
                Invited {day.format(new Date(invitation.created_at))} · not accepted yet
              </small>
            </span>
            <button
              type="button"
              className="rh-text-btn"
              disabled={busy}
              onClick={() =>
                void act(
                  () => agencyApi.cancelInvitation(organisationId, invitation.id),
                  'Invitation cancelled.',
                )
              }
            >
              Cancel
            </button>
          </li>
        ))}
      </ul>
      <form className="rh-attach" onSubmit={invite}>
        <label className="sr-only" htmlFor="client-invite">
          Email address to invite
        </label>
        <input
          id="client-invite"
          type="email"
          placeholder="name@client.co.za"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <button type="submit" className="btn small" disabled={busy || !email.trim()}>
          Invite
        </button>
      </form>
    </section>
  );
}
